<script setup>
import { onMounted, onUnmounted, ref, watch, computed, nextTick } from 'vue'
import { useResultsStore } from '../stores/results-fetcher.js'
import { useStateStore } from '../stores/state.js'
import { useRoute } from 'vue-router'
import EventSwitcher from './EventSwitcher.vue'

const resultsStore = useResultsStore()
const stateStore = useStateStore()
const route = useRoute()
const eventAlias = route.params.eventAlias
resultsStore.fetchResults(eventAlias)
resultsStore.fetchEventDetails(eventAlias)
stateStore.fetchEvents()

const now = ref(Date.now())
const refreshIntervalMs = 30000
const clockIntervalMs = 1000

let refreshResultsTimer = setInterval(() => {
  resultsStore.fetchResults(route.params.eventAlias)
}, refreshIntervalMs)

let clockTimer = null

// Search functionality
const searchQuery = ref('')
const showDropdown = ref(false)
const selectedResultId = ref(null)

// Track recently changed rows
const recentlyChanged = ref({})
let lastResults = []

watch(
  () => resultsStore.results.map(r => ({
    id: r.id,
    pointScore: r.pointScore,
    finish_time: r.finish_time,
    finish_time_gps: r.finish_time_gps,
    dnf: r.dnf,
    abs: r.abs,
  })),
  (newResults) => {
    const nowTime = Date.now()
    if (lastResults.length === 0) {
      lastResults = JSON.parse(JSON.stringify(newResults))
      return
    }
    newResults.forEach((r) => {
      const prev = lastResults.find(lr => lr.id === r.id)
      if (
        !prev ||
        prev.pointScore !== r.pointScore ||
        prev.finish_time !== r.finish_time ||
        prev.finish_time_gps !== r.finish_time_gps ||
        prev.dnf !== r.dnf ||
        prev.abs !== r.abs
      ) {
        recentlyChanged.value[r.id] = nowTime
      }
    })
    lastResults = JSON.parse(JSON.stringify(newResults))
  },
  { immediate: true }
)

// Computed property for filtered results based on search
const filteredResults = computed(() => {
  if (!searchQuery.value.trim()) return []
  const query = searchQuery.value.trim().toLowerCase()
  return resultsStore.results.filter(result => {
    const nameMatch = result.name.toLowerCase().includes(query)
    const numberMatch = result.number.toString().includes(query)
    return (nameMatch || numberMatch) && result.category_id !== 10
  }).slice(0, 10)
})

watch(searchQuery, (newQuery) => {
  showDropdown.value = newQuery.trim().length > 0 && filteredResults.value.length > 0
})

function selectResult(result) {
  selectedResultId.value = result.id
  searchQuery.value = ''
  showDropdown.value = false
  recentlyChanged.value[result.id] = Date.now()
  nextTick(() => {
    const resultRow = document.querySelector(`tr[data-result-id="${result.id}"]`)
    if (resultRow) {
      resultRow.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  })
}

function handleClickOutside(event) {
  if (!event.target.closest('.search-container')) {
    showDropdown.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  clockTimer = setInterval(() => {
    now.value = Date.now()
    const cutoff = Date.now() - 30 * 1000
    for (const id in recentlyChanged.value) {
      if (recentlyChanged.value[id] < cutoff) {
        delete recentlyChanged.value[id]
      }
    }
  }, clockIntervalMs)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  if (clockTimer) clearInterval(clockTimer)
  if (refreshResultsTimer) clearInterval(refreshResultsTimer)
})

// Sorted results: by score desc, then time asc; DNS/DNF at bottom
const sortedResults = computed(() => {
  return resultsStore.results
    .filter(r => r.category_id !== 10)
    .slice()
    .sort((a, b) => {
      // DNS always at the very bottom
      if (a.abs !== b.abs) return a.abs ? 1 : -1
      // DNF below finishers but above DNS
      if (a.dnf !== b.dnf) return a.dnf ? 1 : -1

      const aScore = a.pointScore ?? 0
      const bScore = b.pointScore ?? 0

      // Higher score wins
      if (aScore !== bScore) return bScore - aScore

      // Tiebreaker: shorter elapsed time wins
      const aTime = a.elapsedTime ?? Number.POSITIVE_INFINITY
      const bTime = b.elapsedTime ?? Number.POSITIVE_INFINITY
      return aTime - bTime
    })
})

function getResultClass(result) {
  if (result.dnf) return 'badge bg-danger'
  if (result.abs) return 'badge bg-secondary'
  if (result.finish_time || result.finish_time_gps) return ''
  return 'result-upcoming'
}

function formatTime(dateString) {
  if (!dateString) return ''
  return new Date(dateString).toLocaleTimeString()
}
</script>

<template>
  <div>
    <EventSwitcher />
    <h1>Wyniki rogaining</h1>

    <!-- Search functionality -->
    <div class="search-container mb-3">
      <div class="position-relative">
        <div class="search-input-wrapper">
          <svg class="search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" fill="currentColor"/>
          </svg>
          <input
            type="text"
            v-model="searchQuery"
            class="form-control search-input"
            placeholder="Wyszukaj zawodnika po numerze lub nazwie..."
            @focus="showDropdown = searchQuery.trim().length > 0 && filteredResults.length > 0"
          />
        </div>

        <!-- Search dropdown -->
        <div v-if="showDropdown" class="search-dropdown">
          <div
            v-for="result in filteredResults"
            :key="result.id"
            class="search-dropdown-item"
            @click="selectResult(result)"
          >
            <span class="badge bg-secondary me-2">{{ result.number }}</span>
            <span>{{ result.name }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="table-scroll">
      <div class="settings">
        <span class="badge bg-info">Aktualizacja co 30 sekund</span>
        <span class="badge bg-success">Czas: {{ new Date(now).toLocaleTimeString() }}</span>
      </div>
      <table class="table table-striped">
        <thead>
          <tr>
            <th>#</th>
            <th>Zawodnik</th>
            <th>Start</th>
            <th>Meta</th>
            <th>Czas</th>
            <th>Wynik</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(result, index) in sortedResults"
            :key="result.id"
            :data-result-id="result.id"
            :class="{ 'row-changed': recentlyChanged[result.id] }"
          >
            <td>{{ index + 1 }}</td>
            <td><span class="badge bg-secondary">{{ result.number }}</span> {{ result.name }}</td>
            <td>{{ formatTime(result.start_time) }}</td>
            <td>
              {{ result.finish_time
                ? formatTime(result.finish_time)
                : formatTime(result.finish_time_gps) }}
              <span v-if="!result.finish_time && result.finish_time_gps" class="badge bg-warning">GPS</span>
            </td>
            <td>
              <span v-if="result.dnf || result.abs" :class="getResultClass(result)">
                {{ result.abs ? 'DNS' : 'DNF' }}
              </span>
              <span v-else :class="getResultClass(result)">
                {{ result.timeScore || '' }}
              </span>
            </td>
            <td class="score-cell">
              <span v-if="!result.dnf && !result.abs">
                {{ result.pointScore ?? '' }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.table {
  text-align: left;
}
.table-scroll {
  width: 100%;
  max-height: 80vh;
  overflow-x: auto;
  overflow-y: auto;
}

/* Sticky table header */
.table thead th {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #f8f9fa;
}

.table.table-striped > tbody > tr.row-changed > td,
tbody tr.row-changed > td {
  background: var(--sledz-highlight) !important;
  transition: background 1s;
  font-weight: bold !important;
  animation: highlight-row 1s linear;
}
@keyframes highlight-row {
  0% { background: var(--sledz-highlight-flash); }
  100% { background: var(--sledz-highlight); }
}

.score-cell {
  font-weight: bold;
}

.result-upcoming {
  font-style: italic;
}

/* Search functionality styles */
.search-container {
  max-width: 400px;
}

.search-input-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  pointer-events: none;
  z-index: 10;
}

.search-input {
  padding-left: 40px;
}

.search-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #dee2e6;
  border-top: none;
  border-radius: 0 0 0.375rem 0.375rem;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.search-dropdown-item {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid #f8f9fa;
  display: flex;
  align-items: center;
}

.search-dropdown-item:hover {
  background-color: #f8f9fa;
}

.search-dropdown-item:last-child {
  border-bottom: none;
}
</style>
